import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';
import TempStart from './TempStart';
import { useShellUser } from '../components/AppShell';
import CheckInMap from '../components/CheckInMap';
import GameCard from '../components/games/GameCard';
import GameSheet from '../components/games/GameSheet';
import PredictDialog from '../components/games/PredictDialog';
import CheckInNotice from '../components/games/CheckInNotice';
import {
  apiErrorMessage,
  checkInClosesMs,
  checkInOpensMs,
  flattenScheduleEvents,
  formatWindowTime,
  hasFinalScore,
  isPastEvent,
  isPredictionOpen,
  isWithinCheckInWindow,
  normalizeMediaUrl,
  pickSchoolLogoUrl,
  readPrediction,
  schoolIdOf,
} from '../utils/gameSchedule';
import { attachSquadResults, checkInNotice } from '../utils/gameCards';
import '../styles/Games.css';

function readPosition() {
  if (!navigator.geolocation) {
    return Promise.reject(new Error('Could not get GPS. Allow location access and try again.'));
  }
  return new Promise((resolve, reject) => {
    let pending = 2;
    let denied = false;
    const succeed = (coords) => {
      if (pending < 0) return;
      pending = -1;
      resolve(coords);
    };
    const fail = (err) => {
      if (pending < 0) return;
      if (err && err.code === 1) denied = true;
      pending -= 1;
      if (pending > 0) return;
      reject(new Error(
        denied
          ? 'Could not get GPS. Allow location access and try again.'
          : 'Location is unavailable. Turn on Location Services for your browser and try again.'
      ));
    };
    [
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 10 * 60 * 1000 },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 },
    ].forEach((options) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => succeed({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
        fail,
        options
      );
    });
  });
}

function CheckInScreen({ event, mode, user, error, awarded, submitting, locateTask, onClose, onSubmit }) {
  const [userCoord, setUserCoord] = useState(null);
  const opensMs = checkInOpensMs(event);
  const closesMs = checkInClosesMs(event);
  const tooEarly = opensMs != null && Date.now() < opensMs;
  const logo = normalizeMediaUrl(pickSchoolLogoUrl(user));

  useEffect(() => {
    if (mode !== 'map' || !navigator.geolocation) return undefined;
    let cancelled = false;
    let watchId = null;
    const task = locateTask || readPosition();
    task
      .then((coords) => {
        if (cancelled || !coords) return;
        setUserCoord(coords);
        watchId = navigator.geolocation.watchPosition(
          (pos) => {
            if (cancelled) return;
            setUserCoord({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            });
          },
          () => {},
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [mode, locateTask]);

  return (
    <section className="games-checkin">
      <header className="games-checkin-bar">
        <button type="button" onClick={onClose} aria-label="Back">
          ‹
        </button>
        <h2>Check in</h2>
      </header>
      {mode === 'window' ? (
        <div className="games-checkin-window">
          <h3>{tooEarly ? "Check-in isn't open yet" : 'Check-in is closed'}</h3>
          <p>
            {tooEarly
              ? 'You can check in starting 15 minutes before kickoff. Come back during the window below.'
              : 'The check-in window for this game has ended.'}
          </p>
          <div className="games-window">
            <span>
              <em>Opens</em>
              <strong>{formatWindowTime(opensMs) || 'TBD'}</strong>
            </span>
            <span>
              <em>Closes</em>
              <strong>{formatWindowTime(closesMs) || 'TBD'}</strong>
            </span>
          </div>
        </div>
      ) : null}
      {mode === 'map' ? (
        <div className="games-checkin-stage">
          <CheckInMap
            event={event}
            userCoord={userCoord}
            onLocate={async () => {
              const coords = await readPosition();
              setUserCoord(coords);
              return coords;
            }}
          />
          {userCoord ? null : <p className="games-locating">Finding your location...</p>}
        </div>
      ) : null}
      {mode === 'success' ? (
        <div className="games-checkin-success">
          {logo ? <img src={logo} alt="" /> : <span className="games-logo-fallback" />}
          <h3>Checked in!</h3>
          <p>{`You earned +${awarded} pts for attending tonight's game.`}</p>
        </div>
      ) : null}
      {error ? <p className="games-dialog-error games-checkin-alert">{error}</p> : null}
      <footer className="games-checkin-footer">
        {mode === 'map' ? (
          <button
            type="button"
            className="games-btn is-primary"
            onClick={() => onSubmit(userCoord)}
            disabled={submitting || !!event.already_checked_in}
          >
            {event.already_checked_in
              ? "You're already checked in"
              : submitting
                ? 'Checking in...'
                : 'Check in'}
          </button>
        ) : (
          <button type="button" className="games-btn is-primary" onClick={onClose}>
            {mode === 'success' ? 'Done' : 'Back to games'}
          </button>
        )}
      </footer>
    </section>
  );
}

function GamesPage() {
  const { user, setUser } = useShellUser();
  const [tab, setTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // The open overlay: the game sheet, the compose dialog, or a check-in notice.
  const [sheet, setSheet] = useState(null);
  const [predictEvent, setPredictEvent] = useState(null);
  const [notice, setNotice] = useState(null);
  const [checkIn, setCheckIn] = useState(null);
  const [checkInError, setCheckInError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [awarded, setAwarded] = useState(0);
  const [locateTask, setLocateTask] = useState(null);

  const load = useCallback(async () => {
    const current = await apiService.fetchCurrentUser().catch(() => apiService.getCurrentUser());
    if (current) setUser(current);
    const schoolId = schoolIdOf(current);
    if (!schoolId) {
      setEvents([]);
      return;
    }
    const schedules = await apiService.getSportSchedules(schoolId);
    const bundles = await Promise.all(
      schedules.map(async (sport) => ({
        id: sport.id,
        events: await apiService.getScheduleEvents(sport.id),
      }))
    );
    const byId = {};
    bundles.forEach((bundle) => {
      byId[bundle.id] = bundle.events;
    });
    setEvents(flattenScheduleEvents(schedules, byId, current));
    // The squad winner lines on finished games arrive after the list; no squad, no line.
    apiService
      .getSquadGameWinners()
      .then((winners) => setEvents((prev) => attachSquadResults(prev, winners)))
      .catch(() => {});
  }, [setUser]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const filtered = events.filter((event) => {
    const past = isPastEvent(event);
    return tab === 'past' ? past : !past;
  });

  // Every overlay reads the live row, so a save or a check-in shows at once.
  const liveEvent = (event) => events.find((row) => String(row.id) === String(event.id)) || event;

  const rememberPrediction = (saved) => {
    if (!saved?.id) return;
    setEvents((prev) => prev.map((row) => (
      String(row.id) === String(saved.id)
        ? { ...row, already_predicted: true, my_prediction: saved.my_prediction }
        : row
    )));
  };

  // No Predictions | Check-ins switch on this page: the sheet's tabbed mode is kept for the
  // squads page. A game opens on its predictions; "Checked in" opens its check-ins.
  const openSheet = (event, options = {}) => {
    setPredictEvent(null);
    setSheet({ eventId: event.id, mode: options.mode || 'predictions', tab: options.tab || 'predictions', squadId: options.squadId || null });
  };

  // The card's predict button never shows the Predictions | Check-ins switch: results and an
  // existing pick open the predictions-only view; a fresh pick opens the compose dialog
  // while predictions are still open.
  const openPredict = (event) => {
    if (hasFinalScore(event) || readPrediction(event) || event.already_predicted) {
      openSheet(event, { mode: 'predictions' });
      return;
    }
    if (!isPredictionOpen(event)) return;
    setPredictEvent(event);
  };

  const changePrediction = (event) => {
    setSheet(null);
    if (!isPredictionOpen(event)) return;
    setPredictEvent(event);
  };

  // The gate: a notice explains a closed, early or disabled window; otherwise the map.
  const startCheckIn = (event) => {
    setSheet(null);
    const why = checkInNotice(event, Date.now());
    if (why) {
      setNotice({ event, why });
      return;
    }
    setCheckInError('');
    setAwarded(0);
    setLocateTask(readPosition());
    setCheckIn({ event, mode: 'map' });
  };

  const onCardCheckIn = (event) => {
    if (event.already_checked_in) {
      openSheet(event, { mode: 'checkins' });
      return;
    }
    startCheckIn(event);
  };

  const submitCheckIn = async (coords) => {
    const event = checkIn?.event;
    if (!event || submitting || event.already_checked_in) return;
    setSubmitting(true);
    setCheckInError('');
    try {
      const position = coords || (await readPosition());
      if (!position || position.latitude == null || position.longitude == null) {
        setCheckInError('Could not get GPS. Allow location access and try again.');
        return;
      }
      if (event.latitude == null || event.longitude == null) {
        setCheckInError('This game has no check-in pin yet.');
        return;
      }
      if (!isWithinCheckInWindow(event)) {
        const opensAt = checkInOpensMs(event);
        const early = opensAt != null && Date.now() < opensAt;
        setCheckInError(early ? 'Check-in opens 15 minutes before the game.' : 'Check-in is closed for this game.');
        return;
      }
      const response = await apiService.createScheduleEventCheckIn(event.id, position);
      const points = response?.points_awarded != null ? response.points_awarded : event.check_in_points || 500;
      setEvents((prev) => prev.map((row) => (
        String(row.id) === String(event.id) ? { ...row, already_checked_in: true } : row
      )));
      setUser((prev) => (
        prev ? { ...prev, current_points: (Number(prev.current_points) || 0) + (Number(points) || 0) } : prev
      ));
      setAwarded(Number(points) || 0);
      setCheckIn({ event: { ...event, already_checked_in: true }, mode: 'success' });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.already_checked_in) {
        setEvents((prev) => prev.map((row) => (
          String(row.id) === String(event.id) ? { ...row, already_checked_in: true } : row
        )));
        setCheckIn((prev) => (prev ? { ...prev, event: { ...prev.event, already_checked_in: true } } : prev));
        setCheckInError((Array.isArray(data.already_checked_in) && data.already_checked_in[0]) || "You're already checked in");
        return;
      }
      if (data?.too_early) {
        setCheckInError((Array.isArray(data.too_early) && data.too_early[0]) || 'Check-in opens 15 minutes before the game.');
        return;
      }
      setCheckInError(apiErrorMessage(err, 'Check-in failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (checkIn) {
    return (
      <CheckInScreen
        event={checkIn.event}
        mode={checkIn.mode}
        user={user}
        error={checkInError}
        awarded={awarded}
        submitting={submitting}
        locateTask={locateTask}
        onClose={() => setCheckIn(null)}
        onSubmit={submitCheckIn}
      />
    );
  }

  const sheetEvent = sheet ? events.find((row) => String(row.id) === String(sheet.eventId)) : null;

  return (
    <div className="games-page">
      <div className="games-toolbar">
        <div className="games-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'upcoming'} className={tab === 'upcoming' ? 'is-on' : ''} onClick={() => setTab('upcoming')}>
            Upcoming
          </button>
          <button type="button" role="tab" aria-selected={tab === 'past'} className={tab === 'past' ? 'is-on' : ''} onClick={() => setTab('past')}>
            Past
          </button>
        </div>
      </div>
      {loading ? <p className="games-empty">Loading games...</p> : null}
      {!loading && !filtered.length ? (
        <p className="games-empty">{tab === 'past' ? 'No past games' : 'No upcoming games'}</p>
      ) : null}
      {!loading && filtered.length ? (
        <div className="games-grid">
          {filtered.map((event) => (
            <GameCard
              key={String(event.id)}
              event={event}
              onOpen={(row) => openSheet(row)}
              onSquad={(row, squadId) => openSheet(row, { mode: 'predictions', squadId })}
              onPredict={openPredict}
              onCheckIn={onCardCheckIn}
            />
          ))}
        </div>
      ) : null}
      {sheetEvent ? (
        <GameSheet
          key={`${sheet.eventId}:${sheet.mode}:${sheet.squadId || ''}`}
          event={sheetEvent}
          mode={sheet.mode}
          initialTab={sheet.tab}
          squadId={sheet.squadId}
          onClose={() => setSheet(null)}
          onSaved={rememberPrediction}
          onChangePrediction={changePrediction}
          onCheckIn={startCheckIn}
        />
      ) : null}
      {predictEvent ? (
        <PredictDialog
          event={liveEvent(predictEvent)}
          onClose={() => setPredictEvent(null)}
          onSaved={rememberPrediction}
        />
      ) : null}
      {notice ? <CheckInNotice event={notice.event} notice={notice.why} onClose={() => setNotice(null)} /> : null}
    </div>
  );
}

function GamesGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return <GamesPage />;
}

export default GamesGate;
